// Soulumo creation: Explain → Agree → Record → Create.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, ChevronLeft } from "lucide-react";
import { API, auth, TOK, StepDots, decideDuration, scriptFromSections } from "./shared";
import { TellStep, ThinkingStep, ListStep } from "./TellStep";
import { ClarifyStep } from "./ClarifyStep";
import { RecordOwnStep } from "./RecordOwnStep";
import { MIN_EXPLAIN } from "./ExplainInput";
import { WordsStep } from "./WordsStep";
import { VoiceStep } from "./VoiceStep";
import { CreateStep } from "./CreateStep";
import { CoachPassStep } from "./CoachPassStep";
import { CreatingStep, ReadyStep } from "./ReadyStep";
import { RepeatStep, loadLastCreate, saveLastCreate } from "./RepeatBits";
import { loadSimpleDraft, saveSimpleDraftLocal, saveSimpleDraftServer, clearSimpleDraft } from "./drafts";

const MALE_VOICE_ID = "3BrRQZxtiIYX5Ytyvl1h";
const COPY_CUE = /(same as|exact(ly)? (the )?same|copy of|just like|exactly like|the same one|signature|installation called)/i;
const OWN_VOICE = /(my|own) (own )?voice/i;
const backOf = (step, voice) => (step === "create" && voice === "own" ? "recordOwn" : BACK[step]);
const BACK = { pass: "words", list: "tell", words: "tell", clarify: "tell", voice: "words", recordOwn: "voice", create: "voice", repeat: "tell" };
const RESUMABLE = { thinking: "words", clarify: "tell", pass: "words" };
const STEP_INDEX = { list: 0, tell: 0, thinking: 0, clarify: 0, words: 1, voice: 2, recordOwn: 2, create: 3, repeat: 3, creating: 3, ready: 3 };

const usePollUntilReady = () => React.useCallback(async (j, onTick) => {
  let cur = j;
  let misses = 0;
  for (let i = 0; i < 120 && cur.audio_status === "rendering"; i += 1) {
    await new Promise((r) => setTimeout(r, 2500));
    try {
      cur = (await axios.get(`${API}/api/audio-journeys/${cur.id}`, auth())).data;
      misses = 0;
    } catch (e) {
      // A brief network or server hiccup shouldn't abandon a build that's still running.
      misses += 1;
      if (misses >= 12) throw e;
      continue;
    }
    onTick(cur);
    if (cur.queue_position >= 1 && (cur.queue_eta_seconds || 0) > 150) break;
  }
  return cur;
}, []);

export default function SimpleCreateFlow({ onClose, onCreated, onMoreOptions, onMoreOptionsBack, hidden, initialIntent = "", startRepeat = false, initialPlan = null, fromCoach = null, initialDraft = null, gift = null }) {
  const [step, setStep] = useState(initialPlan ? "words" : initialIntent || startRepeat ? "thinking" : "tell");
  const [listText, setListText] = useState("");
  const [intent, setIntent] = useState(initialIntent);
  const [plan, setPlan] = useState(null);
  const [sections, setSections] = useState([]);
  const [voice, setVoice] = useState("mine");
  const [minutes, setMinutes] = useState(10);
  const [journey, setJourney] = useState(null);
  const [error, setError] = useState("");
  const [last] = useState(loadLastCreate);
  const [repeating, setRepeating] = useState(false);
  const [question, setQuestion] = useState("");
  const [clarified, setClarified] = useState(false);
  const poll = usePollUntilReady();
  const [resume, setResume] = useState(null);
  const fresh = !initialPlan && !initialIntent && !startRepeat && !fromCoach && !initialDraft;
  useEffect(() => {
    if (fresh) loadSimpleDraft().then((s) => s && setResume(s));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (resume && intent.trim()) setResume(null);
    else if (resume) return undefined;
    if ((!intent.trim() && !plan && !listText.trim()) || step === "ready" || step === "creating") return undefined;
    const state = { step: RESUMABLE[step] || step, intent, plan, sections, voice, minutes, listText, saved_at: new Date().toISOString() };
    if (state.step === "words" && !plan) state.step = "tell";
    saveSimpleDraftLocal(state);
    const id = setTimeout(() => saveSimpleDraftServer(state), 800);
    return () => clearTimeout(id);
  }, [step, intent, plan, sections, voice, minutes, listText, resume]);
  const clearDraft = clearSimpleDraft;
  const resumeDraft = (s = resume) => {
    setResume(null);
    setIntent(s.intent || ""); setListText(s.listText || ""); setVoice(s.voice || "mine"); setMinutes(s.minutes || 10);
    if (s.plan) { setPlan(s.plan); setSections(s.sections || []); }
    setStep(s.plan ? s.step : s.listText && !s.intent ? "list" : "tell");
  };
  const startFresh = () => { setResume(null); clearDraft(); };
  const remusic = async (nj) => {
    setJourney(nj); setStep("creating");
    const done = await poll(nj, setJourney);
    setJourney(done); setStep("ready");
  };

  const applyPlan = (p) => {
    setPlan(p); setMinutes(p.duration_minutes || 10); setIntent(p.custom_concern || "");
    setSections((p.sections || []).map((s) => ({ section: s.section, lines: (s.lines || []).map((l) => ({ text: l.text, original: l.original })) })));
    setStep("words");
  };
  useEffect(() => { if (initialPlan) applyPlan(initialPlan); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fromList = async () => {
    setError(""); setStep("thinking");
    try { applyPlan((await axios.post(`${API}/api/audio-journeys/ai/from-list`, { lines_text: listText }, auth())).data); }
    catch { setError("Soulumo couldn't read that list just now. Check there's one line per affirmation and try again."); setStep("list"); }
  };
  const exactWords = () => { setSections((ss) => ss.map((s) => ({ ...s, lines: s.lines.map((l) => ({ ...l, text: l.original || l.text })) }))); setPlan((p) => ({ ...p, changes: [] })); };

  const ask = async (prev = null, override = null) => {
    const text = (prev ? prev.intent : override ?? intent).trim();
    if (text.length > 8000) {
      setError("That's beautifully detailed, but a little long for Soulumo. Please trim it to under 8,000 characters (about 1,300 words). Your words are saved.");
      setStep("tell");
      return;
    }
    if (text.length < MIN_EXPLAIN) {
      setError("Tell Soulumo a little more — one sentence is perfect.");
      setStep("tell");
      return;
    }
    const dur = prev ? prev.minutes : decideDuration(text);
    if (prev) { setIntent(text); setVoice(prev.voice); }
    setRepeating(!!prev);
    setMinutes(dur);
    setError("");
    setStep("thinking");
    try {
      const r = await axios.post(`${API}/api/audio-journeys/ai/ask`, { intent_text: text, duration_minutes: dur }, { ...auth(), timeout: 100000 });
      setPlan(prev?.music_ids?.length ? { ...r.data, music_ids: prev.music_ids, binaural_stages: prev.binaural_stages?.length ? prev.binaural_stages : r.data.binaural_stages } : r.data);
      setSections((r.data.sections || []).map((s) => ({ section: s.section, lines: (s.lines || []).map((l) => ({ text: l.text })) })));
      setStep(prev ? "repeat" : "words");
    } catch (e) {
      const s = e?.response?.status;
      setError(s === 422 ? "Soulumo couldn't read that just now. Your words are saved. Tap Begin to try again."
        : e?.code === "ECONNABORTED" ? "That took longer than usual. Tap Begin to try again."
        : !e?.response ? "Your connection dropped for a moment. Tap Begin to try again."
          : "Soulumo Coach needs another moment. Tap Begin to try again.");
      setStep("tell");
    }
  };

  // Soulumo Coach asks one gentle follow-up when the Explain text is too vague (once per flow).
  const begin = async (text = intent) => {
    if (COPY_CUE.test(text) && OWN_VOICE.test(text)) {
      setStep("thinking");
      try { applyPlan((await axios.get(`${API}/api/audio-journeys/blueprints/match`, { ...auth(), params: { q: text } })).data); return undefined; }
      catch { /* not a signature title, carry on as normal */ }
    }
    if (clarified || text.trim().length < MIN_EXPLAIN) return ask(null, text);
    setStep("thinking");
    setClarified(true);
    try {
      const r = await axios.post(`${API}/api/audio-journeys/ai/clarify`, { intent_text: text.trim() }, { ...auth(), timeout: 20000 });
      if (r.data?.vague && r.data.question) { setQuestion(r.data.question); setStep("clarify"); return undefined; }
    } catch { /* no follow-up — carry on */ }
    return ask(null, text);
  };

  const answerCoach = (answer) => {
    const a = answer.trim();
    const combined = a ? `${intent.trim().replace(/[.!?]*$/, ".")} ${a[0].toUpperCase()}${a.slice(1)}` : intent;
    setIntent(combined);
    ask(null, combined);
  };

  useEffect(() => {
    if (initialDraft) resumeDraft(initialDraft);
    else if (startRepeat && last) ask(last);
    else if (initialIntent) begin(initialIntent);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Phone/browser Back steps back through the flow instead of leaving the page.
  const stepRef = React.useRef(step);
  stepRef.current = step;
  const voiceRef = React.useRef(voice);
  voiceRef.current = voice;
  const hiddenRef = React.useRef(hidden);
  hiddenRef.current = hidden;
  useEffect(() => {
    window.history.pushState({ soulumoFlow: true }, "");
    const onPop = () => {
      const prev = backOf(stepRef.current, voiceRef.current);
      if (hiddenRef.current) { window.history.pushState({ soulumoFlow: true }, ""); onMoreOptionsBack?.(); return; }
      if (prev) { setStep(prev); window.history.pushState({ soulumoFlow: true }, ""); } else onClose();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setError("");
    setStep("creating");
    const payload = {
      name: plan.journey_name, music_ids: plan.music_ids, binaural_stages: plan.binaural_stages,
      affirmation_theme: plan.theme_id, duration_minutes: minutes,
      custom_concern: (plan.custom_concern || intent).slice(0, 600), approved_script: scriptFromSections(sections),
    };
    if (fromCoach?.id) payload.coach_prep_id = fromCoach.id;
    if (gift) { payload.gift_for = gift.for; payload.gift_note = gift.note; }
    if (voice !== "mine") payload.use_clone_voice = false;
    else payload.use_clone_voice = true;
    if (voice === "male") payload.narrator_voice_id = MALE_VOICE_ID;
    try {
      const j = (await axios.post(`${API}/api/audio-journeys`, payload, auth())).data;
      saveLastCreate({ intent: intent.trim(), journey_name: plan.journey_name, music_ids: plan.music_ids,
        binaural_stages: plan.binaural_stages, voice, minutes });
      setJourney(j);
      clearDraft();
      const done = await poll(j, setJourney);
      setJourney(done);
      setStep("ready");
    } catch (e) {
      const capped = e?.response?.status === 402;
      const d = e?.response?.data?.detail;
      if (d === "gift_limit" || d === "premium_required") {
        setError(d === "gift_limit" ? "You've made both of this month's gifts. More arrive on the 1st." : "Gifts are part of My Voice Premium.");
        setStep("create");
        return;
      }
      if (e?.response?.status === 409 && typeof d === "string") {
        const [code, resets] = d.split(":");
        setError(code === "over_allowance"
          ? `You've used this month's voice allowance${resets ? ` (it resets on ${new Date(`${resets}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long" })})` : ""}. Go back and choose a Soulumo voice, or add credits.`
          : code === "clone_not_enabled" ? "My voice isn't switched on for your account. Go back and choose a Soulumo voice." : "Your voice isn't ready yet. Go back and record it, or choose a Soulumo voice.");
        setStep("create");
        return;
      }
      const why = Array.isArray(d) ? d.map((x) => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join("; ") : typeof d === "string" ? d : "";
      if (e?.response?.status === 422) console.error("create 422", d);
      setError(capped ? "You've used this month's installations in your voice. Add credits, or choose a Soulumo voice." : `Something interrupted the build — please tap Create again.${why ? ` (${why})` : ""}`);
      setStep(repeating ? "repeat" : "create");
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[115] flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
      style={{ display: hidden ? "none" : undefined, background: "rgba(6,10,20,0.88)", backdropFilter: "blur(18px)", paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))" }}
      data-testid="simple-create-modal">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.42, ease: [0.22, 0.61, 0.36, 1] }}
        className="w-full max-w-xl rounded-3xl p-6 sm:p-9 relative"
        style={{ background: "radial-gradient(ellipse at 50% 0%, #1a3058 0%, #0d1f3a 55%, #091629 100%)", border: `1px solid ${TOK.goldMist}` }}>
        {gift && <p className="-mt-2 mb-5 text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: TOK.gold }} data-testid="simple-create-gift-banner">A gift for {gift.for}</p>}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            {BACK[step] && (
              <button type="button" onClick={() => setStep(backOf(step, voice))} aria-label="Back" data-testid="simple-create-back"
                className="w-9 h-9 rounded-full inline-flex items-center justify-center" style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}>
                <ChevronLeft size={16} />
              </button>
            )}
            <StepDots step={STEP_INDEX[step]} />
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="simple-create-close"
            className="w-10 h-10 rounded-full inline-flex items-center justify-center" style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}>
            <X size={16} />
          </button>
        </div>
        {step === "tell" && resume && (
          <div className="mb-6 rounded-2xl p-4" style={{ background: "rgba(215,187,122,0.1)", border: `1px solid ${TOK.gold}` }} data-testid="simple-resume">
            <p className="text-lg" style={{ color: TOK.cream, fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Welcome back. Your last installation is saved, just as you left it.</p>
            {resume.intent && <p className="mt-1 text-sm italic line-clamp-2" style={{ color: TOK.creamMist }}>“{resume.intent.slice(0, 140)}{resume.intent.length > 140 ? "…" : ""}”</p>}
            <div className="mt-3 flex gap-2">
              <button type="button" onClick={() => resumeDraft()} data-testid="simple-resume-yes" className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.18em]" style={{ background: TOK.gold, color: TOK.ink }}>Pick up where I left off</button>
              <button type="button" onClick={startFresh} data-testid="simple-resume-no" className="rounded-full px-4 py-2 text-xs" style={{ color: TOK.creamMist }}>Start fresh</button>
            </div>
          </div>
        )}
        {step === "tell" && <TellStep intent={intent} setIntent={setIntent} onSubmit={() => begin()} onMoreOptions={onMoreOptions} error={error}
          last={last?.intent && last?.music_ids?.length ? last : null} onRepeat={() => ask(last)} onOwnWords={() => { setError(""); setStep("list"); }} />}
        {step === "list" && <ListStep text={listText} setText={setListText} onSubmit={fromList} error={error} />}
        {step === "words" && fromCoach && (
          <p className="mb-4 text-base italic" style={{ color: "rgba(241,236,221,0.85)", fontFamily: '"Cormorant Garamond", Georgia, serif' }} data-testid="simple-from-coach">
            {fromCoach.is_owner
              ? "You're voicing this one yourself. Record it once, in your cloned voice or line by line, and everyone with your link can listen free."
              : <>{fromCoach.coach_name} has created a Soulumo for you{fromCoach.client_name ? `, ${fromCoach.client_name}` : ""}.{fromCoach.note ? ` “${fromCoach.note}”` : ""} Your personalised affirmations are ready. Now make them yours.</>}
          </p>
        )}
        {step === "words" && !fromCoach && !!plan?.changes?.length && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm" data-testid="simple-tidy-note"
            style={{ background: "rgba(215,187,122,0.08)", color: "rgba(241,236,221,0.8)" }}>
            <span>Soulumo tidied {plan.changes.length} line{plan.changes.length > 1 ? "s" : ""}.</span>
            <button type="button" onClick={exactWords} className="underline" data-testid="simple-exact-words">Use my exact words</button>
          </div>
        )}
        {step === "repeat" && plan && (
          <RepeatStep plan={plan} sections={sections} voice={voice} minutes={minutes} onCreate={create} error={error}
            onReview={() => { setRepeating(false); setStep("words"); }} />
        )}
        {step === "thinking" && <ThinkingStep />}
        {step === "clarify" && <ClarifyStep question={question} onAnswer={answerCoach} onSkip={() => ask()} />}
        {step === "words" && plan && <WordsStep plan={plan} sections={sections} setSections={setSections} approveLabel={fromCoach && !fromCoach.is_owner ? "Make it yours" : null} hideReExplain={!!fromCoach}
          onApprove={() => setStep(fromCoach && fromCoach.access !== "member" ? "pass" : "voice")} onReExplain={() => setStep("tell")} />}
        {step === "pass" && fromCoach && <CoachPassStep fromCoach={fromCoach} onCovered={() => setStep("voice")} />}
        {step === "voice" && <VoiceStep voice={voice} setVoice={setVoice} onContinue={() => setStep("create")} onRecordMyself={() => { setVoice("own"); setStep("recordOwn"); }} />}
        {step === "recordOwn" && <RecordOwnStep sections={sections} onContinue={() => setStep("create")} />}
        {step === "create" && plan && (
          <CreateStep plan={plan} setPlan={setPlan} intent={intent} minutes={minutes} setMinutes={setMinutes} onCreate={create} onMoreOptions={onMoreOptions} error={error} />
        )}
        {step === "creating" && <CreatingStep journey={journey} />}
        {step === "ready" && journey && <ReadyStep journey={journey} onDone={() => onCreated(journey)} onRemusic={remusic} />}
      </motion.div>
    </motion.div>
  );
}
